/**
 * SurveyCycles — HR cycle management page for Employee Survey.
 *
 * Allows HR / Admin to create, edit, activate, and close survey cycles.
 * Activating notifies all targeted employees (or all active employees if no
 * participant subset was chosen) and opens the survey form for submissions.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { deleteData, fetchData, postData, putData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { StandardTablePagination } from "components/Pagination/StandardTablePagination";
import { PAGINATION_CONFIG } from "utils/paginationConfig";
import {
  surveyCyclesUrl,
  surveyCycleDetailUrl,
  activateSurveyCycleUrl,
  closeSurveyCycleUrl,
  surveyTemplatesUrl,
  viewActiveUsers,
} from "../../../Service/Apis";

interface EmployeeOption {
  id: string;
  label: string;
  email: string;
}

interface SurveyCycleItem {
  id: string;
  name: string;
  description: string;
  start_date: string | null;
  end_date: string | null;
  status: "draft" | "active" | "closed";
  template: string | null;
  template_name: string | null;
  participants?: string[];
  participant_count?: number;
  response_count: number;
}

interface CycleFormData {
  name: string;
  description: string;
  start_date: string;
  end_date: string;
}

const defaultForm: CycleFormData = { name: "", description: "", start_date: "", end_date: "" };

/** Maps a cycle status to the MUI Chip color used to display it. */
const statusColor = (status: string): "default" | "success" | "error" => {
  if (status === "active") return "success";
  if (status === "closed") return "error";
  return "default";
};

/**
 * SurveyCycles — HR/Admin page for managing Employee Survey cycles.
 *
 * Lists cycles (with status/search/pagination) and lets HR create or edit a
 * draft cycle (name, dates, assigned template, optional participant subset),
 * then activate it (locks in participants, emails everyone targeted) or
 * close it early. Non-HR/Admin users are redirected straight to the survey
 * form, since this page is management-only.
 */
const SurveyCycles: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

  useEffect(() => {
    if (selectedRole !== "HR" && selectedRole !== "Admin") {
      navigate("/pms/employee-survey/take", { replace: true });
    }
  }, [selectedRole, navigate]);

  const [cycles, setCycles] = useState<SurveyCycleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, closed: 0 });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<SurveyCycleItem | null>(null);
  const [form, setForm] = useState<CycleFormData>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [allTemplates, setAllTemplates] = useState<Array<{ template_id: string; name: string; active_cycle_count: number }>>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<EmployeeOption[]>([]);
  const [participantSearch, setParticipantSearch] = useState("");

  const [activateTarget, setActivateTarget] = useState<SurveyCycleItem | null>(null);
  const [closeTarget, setCloseTarget] = useState<SurveyCycleItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SurveyCycleItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activatingCycleId, setActivatingCycleId] = useState<string | null>(null);

  /** Builds the cycles list URL from the current page/rowsPerPage/status/search filters. */
  const buildUrl = () => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("page_size", String(rowsPerPage));
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (searchQuery) params.set("search", searchQuery);
    return `${surveyCyclesUrl}?${params.toString()}`;
  };

  /** (Re)loads the paginated cycles list (and summary stats) for the current filters. */
  const fetchCycles = () => {
    setLoading(true);
    dispatch(fetchData(buildUrl()))
      .then((res: any) => {
        if (res) {
          setCycles(res.results ?? res);
          setTotalCount(res.count ?? (res.results ?? res).length);
          if (res.stats) setStats(res.stats);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCycles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      fetchCycles();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  /** Loads all templates, for the "assign template" dropdown in the create/edit dialog. */
  const fetchTemplates = () => {
    dispatch(fetchData(`${surveyTemplatesUrl}?page_size=100`)).then((res: any) => {
      if (res) setAllTemplates(res.results ?? res);
    });
  };

  /** Loads all active employees, for the optional participant-subset picker. */
  const fetchEmployees = () => {
    dispatch(fetchData(`${viewActiveUsers}?page_size=500`)).then((res: any) => {
      const list = res?.results ?? res ?? [];
      setAllEmployees(
        list
          .map((e: any) => ({
            id: String(e.id),
            label: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email,
            email: e.email,
          }))
          .sort((a: EmployeeOption, b: EmployeeOption) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
          )
      );
    });
  };

  /** Opens the dialog pre-filled for a brand-new draft cycle. */
  const openCreateDialog = () => {
    setEditingCycle(null);
    setForm(defaultForm);
    setSelectedTemplateId("");
    setSelectedParticipants([]);
    setParticipantSearch("");
    setFormError("");
    fetchTemplates();
    fetchEmployees();
    setDialogOpen(true);
  };

  /** Opens the dialog pre-filled with an existing (draft) cycle's data for editing. */
  const openEditDialog = (cycle: SurveyCycleItem) => {
    setEditingCycle(cycle);
    setForm({
      name: cycle.name,
      description: cycle.description,
      start_date: cycle.start_date ?? "",
      end_date: cycle.end_date ?? "",
    });
    setSelectedTemplateId(cycle.template ?? "");
    setParticipantSearch("");
    setFormError("");
    fetchTemplates();
    fetchEmployees();
    const existing: EmployeeOption[] = (cycle.participants ?? []).map((id: string) => ({
      id,
      label: id,
      email: "",
    }));
    setSelectedParticipants(existing);
    setDialogOpen(true);
  };

  /**
   * `selectedParticipants` starts out with only `{id, email: ""}` placeholders
   * when editing an existing cycle (the API only returns participant IDs).
   * This backfills each placeholder's display name/email once the full
   * employee directory has loaded.
   */
  const resolvedParticipants: EmployeeOption[] = useMemo(() => {
    if (allEmployees.length === 0) return selectedParticipants;
    return selectedParticipants.map((p) => {
      if (p.label !== p.id) return p;
      const match = allEmployees.find((e) => e.id === p.id);
      return match ?? p;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployees, selectedParticipants]);

  /** Fast membership lookup for "is this employee already selected as a participant?". */
  const selectedParticipantIdSet = useMemo(
    () => new Set(resolvedParticipants.map((p) => p.id)),
    [resolvedParticipants]
  );

  /** Renders the checkbox list of employees in the participant picker, filtered by `participantSearch`. */
  const participantListItems = useMemo(() => {
    const q = participantSearch.toLowerCase();
    const filtered = allEmployees.filter(
      (e) => !q || e.label.toLowerCase().includes(q) || e.email.toLowerCase().includes(q)
    );
    if (filtered.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: "center" }}>
          No employees match "{participantSearch}"
        </Typography>
      );
    }
    return filtered.map((employee) => {
      const isSelected = selectedParticipantIdSet.has(employee.id);
      return (
        <FormControlLabel
          key={employee.id}
          control={
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => {
                setSelectedParticipants((prev) =>
                  isSelected ? prev.filter((p) => p.id !== employee.id) : [...prev, employee]
                );
              }}
              sx={{ py: 0.25, pl: 1.5 }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" lineHeight={1.3}>{employee.label}</Typography>
              <Typography variant="caption" color="text.secondary" lineHeight={1.2}>{employee.email}</Typography>
            </Box>
          }
          sx={{
            display: "flex", alignItems: "center", m: 0, px: 0.5, py: 0.5, width: "100%",
            "&:hover": { bgcolor: "action.hover" },
            borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 },
          }}
        />
      );
    });
  }, [allEmployees, participantSearch, selectedParticipantIdSet]);

  /** Validates the form, then creates or updates the (draft) cycle via the API. */
  const handleSave = async () => {
    if (!form.name) {
      setFormError("Cycle name is required.");
      return;
    }
    setSaving(true);
    setFormError("");

    const payload = {
      ...form,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      participants: resolvedParticipants.map((p) => p.id),
      template: selectedTemplateId || null,
    };

    try {
      if (editingCycle) {
        await dispatch(putData(surveyCycleDetailUrl(editingCycle.id), payload));
        showToast("Cycle updated.", "success", false);
      } else {
        await dispatch(postData(surveyCyclesUrl, payload));
        showToast("Cycle created.", "success", false);
      }
      setDialogOpen(false);
      fetchCycles();
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.non_field_errors?.[0] || data?.detail || data?.end_date?.[0];
      setFormError(detail ?? "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Activates the cycle selected via the confirmation dialog: materializes
   * the participant snapshot and triggers bulk notification emails. Because
   * that email fan-out can take a while for large participant lists, the
   * confirmation dialog auto-closes after 10s (showing a "sending..." state
   * on the row via `activatingCycleId`) rather than blocking the whole UI on
   * the request.
   */
  const handleActivate = async () => {
    if (!activateTarget) return;
    const targetId = activateTarget.id;
    setActionLoading(true);
    let dialogClosed = false;

    const timer = setTimeout(() => {
      dialogClosed = true;
      setActivateTarget(null);
      setActionLoading(false);
      setActivatingCycleId(targetId);
    }, 10_000);

    try {
      await dispatch(postData(activateSurveyCycleUrl(targetId), {}, true));
      showToast("Cycle activated. Employees have been notified.", "success", false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to activate cycle.";
      showToast(msg, "warning", false);
    } finally {
      clearTimeout(timer);
      setActivatingCycleId(null);
      setActionLoading(false);
      if (!dialogClosed) setActivateTarget(null);
      fetchCycles();
    }
  };

  /** Closes the active cycle selected via the confirmation dialog (no more submissions accepted). */
  const handleClose = async () => {
    if (!closeTarget) return;
    setActionLoading(true);
    try {
      await dispatch(postData(closeSurveyCycleUrl(closeTarget.id), {}, true));
      showToast("Cycle closed.", "success", false);
      setCloseTarget(null);
      fetchCycles();
    } catch {
      showToast("Failed to close cycle.", "warning", false);
    } finally {
      setActionLoading(false);
    }
  };

  /** Deletes the cycle selected via the confirmation dialog (`deleteTarget`). */
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await dispatch(deleteData(surveyCycleDetailUrl(deleteTarget.id)));
      showToast("Cycle deleted.", "success", false);
      setDeleteTarget(null);
      fetchCycles();
    } catch {
      showToast("Failed to delete cycle.", "error", false);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Employee Survey Cycles</PageTitle>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total", value: stats.total, color: "#1976d2" },
          { label: "Active", value: stats.active, color: "#2e7d32" },
          { label: "Draft", value: stats.draft, color: "#ed6c02" },
          { label: "Closed", value: stats.closed, color: "#d32f2f" },
        ].map((s) => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card elevation={2}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight="bold" color={s.color}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
        <TextField
          size="small"
          label="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <Box flexGrow={1} />
        <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openCreateDialog}>
          New Cycle
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
                "& .MuiTableCell-head": { color: "white", fontWeight: 700, fontSize: "0.82rem", py: 1.5 },
              }}
            >
              <TableCell>Cycle Name</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Responses</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : cycles.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No cycles found.</Typography></TableCell></TableRow>
            ) : (
              cycles.map((cycle, idx) => (
                <TableRow
                  key={cycle.id}
                  hover
                  sx={{
                    bgcolor: idx % 2 === 0 ? "white" : "grey.50",
                    borderLeft: 4,
                    borderColor: cycle.status === "active" ? "success.main" : cycle.status === "closed" ? "error.light" : "grey.300",
                  }}
                >
                  <TableCell sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontWeight="medium">{cycle.name}</Typography>
                    {cycle.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {cycle.description.length > 60 ? cycle.description.slice(0, 60) + "…" : cycle.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {cycle.template_name ? (
                      <Chip label={cycle.template_name} size="small" variant="outlined" color="primary" />
                    ) : (
                      <Typography variant="caption" color="error.main" fontStyle="italic">No template</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.5 }}>{cycle.start_date || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>{cycle.end_date || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>{cycle.response_count}</TableCell>
                  <TableCell sx={{ py: 1.5 }}>
                    {activatingCycleId === cycle.id ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} color="success" thickness={5} />
                        <Typography variant="caption" color="text.secondary" fontStyle="italic">Activating…</Typography>
                      </Box>
                    ) : (
                      <Chip
                        label={cycle.status.charAt(0).toUpperCase() + cycle.status.slice(1)}
                        color={statusColor(cycle.status)}
                        size="small"
                        sx={{ fontWeight: 600, minWidth: 64 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5, whiteSpace: "nowrap" }}>
                    <Tooltip title="View dashboard">
                      <IconButton size="small" color="primary" onClick={() => navigate(`/pms/employee-survey/hr-dashboard?cycle=${cycle.id}`)} sx={{ mr: 1 }}>
                        <BarChartIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={cycle.status === "closed" ? "Cycle is closed" : cycle.status === "active" ? "Disable cycle" : "Enable cycle"}>
                      <span>
                        <Switch
                          size="small"
                          checked={cycle.status === "active"}
                          disabled={cycle.status === "closed"}
                          color="success"
                          onChange={() => {
                            if (cycle.status === "draft") setActivateTarget(cycle);
                            else if (cycle.status === "active") setCloseTarget(cycle);
                          }}
                        />
                      </span>
                    </Tooltip>
                    {cycle.status === "draft" && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(cycle)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {cycle.status === "draft" && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(cycle)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <StandardTablePagination
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { minHeight: isMobile ? undefined : "55vh" } }}
      >
        <DialogTitle>{editingCycle ? "Edit Cycle" : "Create Employee Survey Cycle"}</DialogTitle>
        <DialogContent dividers>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            label="Cycle Name"
            fullWidth
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={2} mb={2} flexDirection={{ xs: "column", sm: "row" }}>
            <TextField
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              helperText="Optional — defaults to activation date"
            />
            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              helperText="Optional — defaults to 1 month after start"
            />
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={editingCycle !== null && editingCycle.status !== "draft"}>
            <InputLabel>Survey Template</InputLabel>
            <Select
              value={selectedTemplateId}
              label="Survey Template"
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <MenuItem value=""><em>No template</em></MenuItem>
              {allTemplates.map((t) => (
                <MenuItem key={t.template_id} value={t.template_id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              Participants
              <Typography component="span" variant="caption" color="text.primary" ml={0.75}>
                (leave empty to notify all active employees)
              </Typography>
            </Typography>

            <Box display="flex" gap={1} alignItems="center" minHeight={28} mb={0.5} mt={0.75}>
              <Button
                size="small"
                variant="text"
                sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0 }}
                onClick={() => setSelectedParticipants(allEmployees)}
                disabled={resolvedParticipants.length === allEmployees.length}
              >
                Select all ({allEmployees.length})
              </Button>
              {resolvedParticipants.length > 0 && (
                <>
                  <Typography variant="caption" color="text.disabled">·</Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ textTransform: "none", fontSize: "0.75rem", p: 0, minWidth: 0, color: "error.main" }}
                    onClick={() => setSelectedParticipants([])}
                  >
                    Clear
                  </Button>
                  <Chip label={`${resolvedParticipants.length} selected`} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
                </>
              )}
            </Box>

            <TextField
              size="small"
              fullWidth
              placeholder="Search employees…"
              value={participantSearch}
              onChange={(e) => setParticipantSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 0.5 }}
            />
            <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: "auto", borderRadius: 1 }}>
              {participantListItems}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingCycle ? "Save Changes" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirm */}
      <Dialog open={!!activateTarget} onClose={() => setActivateTarget(null)}>
        <DialogTitle>Enable Cycle</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Enable <strong>{activateTarget?.name}</strong>? All targeted employees will be notified
            and the survey will accept anonymous responses.
          </Typography>
          {activateTarget && !(activateTarget as any).template && (
            <Alert severity="error" sx={{ mb: 2 }}>
              No template is assigned to this cycle. Create/select a template in{" "}
              <strong>Employee Survey → Templates</strong>, then edit this cycle to assign it before activating.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleActivate}
            disabled={actionLoading || !(activateTarget as any)?.template}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Enable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Confirm */}
      <Dialog open={!!closeTarget} onClose={() => setCloseTarget(null)}>
        <DialogTitle>Disable Cycle</DialogTitle>
        <DialogContent>
          <Typography>Disable <strong>{closeTarget?.name}</strong>? No further submissions will be accepted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClose} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Cycle</DialogTitle>
        <DialogContent>
          <Typography>Permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SurveyCycles;
